import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";

export default function Profile() {
    const { user, logout } = useAuth();
    if (!user) return null;
    return (
        <div className="container-edit py-10 lg:py-14 max-w-3xl">
            <div className="label-eyebrow">Account</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                Profile
            </h1>

            <div className="mt-10 card-flat p-8" data-testid="profile-card">
                <div className="flex items-center gap-5">
                    <div className="h-16 w-16 bg-[#0D1B3E] text-[#F5F0E8] grid place-items-center font-display text-2xl">
                        {user.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                        <div className="font-display text-2xl tracking-tight" data-testid="profile-name">
                            {user.name}
                        </div>
                        <div className="text-sm text-[#0D1B3E]/70" data-testid="profile-email">
                            {user.email}
                        </div>
                    </div>
                </div>

                <hr className="gold-rule my-7" />

                <Row label="Plan" value={user.plan_key} testId="profile-plan" />
                <Row label="Role" value={user.role} />
                <Row
                    label="Member since"
                    value={new Date(user.created_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/app/pricing" className="btn-primary" data-testid="profile-upgrade-button">
                    Manage plan
                </Link>
                <Link
                    to="/app/billing/portal"
                    className="btn-secondary"
                    data-testid="profile-portal-button"
                >
                    Billing portal
                </Link>
                <button onClick={logout} className="btn-secondary" data-testid="profile-logout-button">
                    Sign out
                </button>
                {user.role === "admin" && (
                    <>
                        <Link to="/app/admin/analytics" className="btn-secondary" data-testid="profile-analytics-button">
                            Analytics
                        </Link>
                        <Link to="/app/admin/reset-link" className="btn-secondary" data-testid="profile-reset-link-button">
                            Reset links
                        </Link>
                    </>
                )}
            </div>

            {user.role === "admin" ? (
                <div className="mt-12 border border-[#0D1B3E]/15 bg-white/60 p-6 text-sm text-[#0D1B3E]/70" data-testid="profile-admin-delete-note">
                    <span className="font-medium text-[#0D1B3E]">Account deletion:</span>{" "}
                    the administrator account cannot be deleted from the app. All other
                    accounts can self-delete under Account → Danger zone (30-day grace
                    period, then permanent GDPR Art. 17 erasure).
                </div>
            ) : (
                <DangerZone logout={logout} />
            )}

            <div className="mt-14 flex items-center gap-3 text-[#0D1B3E]/50">
                <EuStars size={22} />
                <span className="text-xs uppercase tracking-[0.22em]">
                    EuroLaw Hub workspace
                </span>
            </div>
        </div>
    );
}

function DangerZone({ logout }) {
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0 closed · 1 warning · 2 confirm
    const [confirmText, setConfirmText] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setError("");
        if (confirmText.trim().toUpperCase() !== "DELETE") {
            setError("Type DELETE (in capitals) to confirm.");
            return;
        }
        if (!password) {
            setError("Enter your password to confirm.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/account/delete", {
                password,
                confirm_text: confirmText.trim(),
            });
            toast.success(data.message || "Account deactivated.");
            logout();
            navigate("/", { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not delete the account. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-12 border border-red-300 bg-red-50/50 p-7" data-testid="profile-danger-zone">
            <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle size={17} />
                <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    Danger zone
                </span>
            </div>
            <h2 className="font-display text-xl mt-3 tracking-tight text-[#0D1B3E]">
                Delete this account
            </h2>

            {step === 0 && (
                <>
                    <p className="mt-2 text-sm text-[#0D1B3E]/70 max-w-lg">
                        Permanently remove your account, documents, conversations and all
                        personal data (GDPR Art. 17). There is a 30-day grace period during
                        which you can change your mind.
                    </p>
                    <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="mt-5 px-5 py-2.5 border border-red-700 text-red-800 text-sm font-medium hover:bg-red-700 hover:text-white transition-colors"
                        data-testid="delete-account-open"
                    >
                        Delete account…
                    </button>
                </>
            )}

            {step === 1 && (
                <div className="mt-4" data-testid="delete-account-warning">
                    <p className="text-sm text-[#0D1B3E]/85 font-medium">
                        Please read carefully. This is what happens when you confirm:
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-[#0D1B3E]/75 list-disc pl-5">
                        <li>Your account is <strong>deactivated immediately</strong> and you are signed out.</li>
                        <li>Any active subscription is <strong>cancelled</strong>, no further charges.</li>
                        <li>
                            After a <strong>30-day grace period</strong>, all personal data
                            (documents, conversations, watchdogs, usage records) is{" "}
                            <strong>permanently and irreversibly erased</strong>.
                        </li>
                        <li>
                            Within those 30 days you can restore everything by signing back
                            in and confirming.
                        </li>
                    </ul>
                    <div className="mt-5 flex gap-3">
                        <button
                            type="button"
                            onClick={() => setStep(2)}
                            className="px-5 py-2.5 bg-red-700 text-white text-sm font-medium hover:bg-red-800 transition-colors"
                            data-testid="delete-account-continue"
                        >
                            I understand, continue
                        </button>
                        <button
                            type="button"
                            onClick={() => setStep(0)}
                            className="btn-secondary text-sm"
                            data-testid="delete-account-cancel-1"
                        >
                            Keep my account
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="mt-4 max-w-md" data-testid="delete-account-confirm">
                    <label className="block">
                        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                            Type DELETE to confirm
                        </span>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="field"
                            placeholder="DELETE"
                            data-testid="delete-account-confirm-text"
                        />
                    </label>
                    <label className="block mt-4">
                        <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                            Your password
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="field"
                            autoComplete="current-password"
                            data-testid="delete-account-password"
                        />
                    </label>
                    {error && (
                        <div
                            className="mt-3 text-sm border border-red-500/40 bg-red-50 text-red-900 px-3 py-2"
                            data-testid="delete-account-error"
                        >
                            {error}
                        </div>
                    )}
                    <div className="mt-5 flex gap-3">
                        <button
                            type="button"
                            onClick={submit}
                            disabled={busy}
                            className="px-5 py-2.5 bg-red-700 text-white text-sm font-medium hover:bg-red-800 transition-colors disabled:opacity-60"
                            data-testid="delete-account-submit"
                        >
                            {busy ? "Deleting…" : "Permanently delete my account"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setStep(0); setConfirmText(""); setPassword(""); setError(""); }}
                            className="btn-secondary text-sm"
                            data-testid="delete-account-cancel-2"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, testId }) {
    return (
        <div className="flex items-center justify-between py-3 border-b border-[#0D1B3E]/10 last:border-b-0">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/60">
                {label}
            </span>
            <span className="text-sm font-medium capitalize" data-testid={testId}>
                {value}
            </span>
        </div>
    );
}
